import React from "react";
import { IconButton } from "../ui/IconButton";
import "./StandardModal.css";

export type ModalActionVariant = "primary" | "secondary" | "danger" | "neutral";

export interface ModalAction {
  label: string;
  onClick?: () => void;
  variant?: ModalActionVariant;
  icon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  /** If true, render as type="submit" for form submission (primary or extra) */
  submit?: boolean;
}

export interface ModalActionsProps {
  primary?: ModalAction;
  secondary?: ModalAction;
  danger?: ModalAction;
  /** Extra buttons (e.g. Verwijderen on the left) */
  extra?: ModalAction[];
  /** Form id - when set, primary button with submit:true will use form={formId} */
  formId?: string;
  /** Primary button variant for role-specific styling (AddPartijModal) */
  primaryVariant?: "default" | "aanvrager" | "wederpartij" | "factuurpartij";
}

const variantToIconButtonVariant: Record<
  ModalActionVariant,
  "brandSolid" | "brandSubtle" | "neutral"
> = {
  primary: "brandSolid",
  secondary: "brandSubtle",
  danger: "brandSubtle",
  neutral: "neutral",
};

const primaryVariantStyles: Record<string, React.CSSProperties> = {
  aanvrager: {
    backgroundColor: "var(--btn-dark-bg)",
    borderColor: "var(--btn-dark-border)",
  },
  wederpartij: {
    backgroundColor: "var(--btn-dark-bg)",
    borderColor: "var(--btn-dark-border)",
  },
  factuurpartij: {
    backgroundColor: "var(--btn-dark-bg)",
    borderColor: "var(--btn-dark-border)",
  },
};

export function ModalActions({
  primary,
  secondary,
  danger,
  extra = [],
  formId,
  primaryVariant = "default",
}: ModalActionsProps) {
  const hasActions = primary || secondary || danger || extra.length > 0;
  if (!hasActions) return null;

  return (
    <div className="modal-actions modal-actions--standard">
      <div className="modal-actions__left">
        {extra.map((action, i) => (
          <IconButton
            key={i}
            type={action.submit && formId ? "submit" : "button"}
            form={action.submit && formId ? formId : undefined}
            variant={variantToIconButtonVariant[action.variant ?? "neutral"]}
            onClick={action.submit ? undefined : action.onClick}
            disabled={action.disabled || action.loading}
            icon={action.icon}
            style={
              action.variant === "danger"
                ? {
                    backgroundColor: "var(--btn-dark-bg)",
                    borderColor: "var(--btn-dark-border)",
                    color: "white",
                  }
                : undefined
            }
          >
            {action.loading ? "..." : action.label}
          </IconButton>
        ))}
      </div>
      <div className="modal-actions__right">
        {secondary && (
          <IconButton
            variant="brandSubtle"
            onClick={secondary.onClick}
            disabled={secondary.disabled || secondary.loading}
            icon={secondary.icon}
          >
            {secondary.loading ? "..." : secondary.label}
          </IconButton>
        )}
        {danger && (
          <IconButton
            variant="brandSolid"
            onClick={danger.onClick}
            disabled={danger.disabled || danger.loading}
            icon={danger.icon}
            style={{
              backgroundColor: "var(--btn-dark-bg)",
              borderColor: "var(--btn-dark-border)",
              color: "white",
            }}
          >
            {danger.loading ? "..." : danger.label}
          </IconButton>
        )}
        {primary && (
          <IconButton
            type={primary.submit ? "submit" : "button"}
            form={primary.submit && formId ? formId : undefined}
            variant="brandSolid"
            onClick={primary.submit ? undefined : primary.onClick}
            disabled={primary.disabled || primary.loading}
            icon={primary.icon}
            style={
              primaryVariant && primaryVariant !== "default"
                ? primaryVariantStyles[primaryVariant]
                : undefined
            }
          >
            {primary.loading ? "..." : primary.label}
          </IconButton>
        )}
      </div>
    </div>
  );
}
